import { ScaleLinear } from 'd3-scale';
import { isUndefined } from 'lodash-es';

import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import { AnnotationsNumericalDisplay } from '../../../core/components/annotations/components/displays/AnnotationsNumericalDisplay.js';
import { AnnotationsTimeDisplay } from '../../../core/components/annotations/components/displays/AnnotationsTimeDisplay.js';
import { AnnotationsScrollWrapper } from '../../../core/components/annotations/components/overlays/AnnotationsScrollWrapper.js';
import {
  COMPACT_ANNOTATIONS_DIMENSIONS,
  DEFAULT_INTEGRATION_TEXT_OVERFLOW,
  SCROLLBAR_THRESHOLD,
} from '../../../core/components/annotations/constants.js';
import { AnnotationsActionsPayload } from '../../../core/components/annotations/types/annotations.js';
import type { SelectionState } from '../../../core/components/annotations/types/state.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import type { DateScales } from '../../../core/types/scales.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useXYChartAnnotationsConfig } from '../../hooks/useXYChartAnnotationsConfig.js';
import { useXYChartAnnotationsData } from '../../hooks/useXYChartAnnotationsData.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartScales } from '../../hooks/useXYChartScales.js';
import { useSetState } from '../../store/selectors.js';
import { isTimeScale } from '../../types/guards.js';
import { getCurrentScale } from '../../utils/get-scale.js';
import { AREAS } from '../../utils/grid-templates.js';

type XYChartAnnotationsAreaProps = {
  compact?: boolean;
};

export const XYChartAnnotationsArea = ({
  compact = false,
}: XYChartAnnotationsAreaProps) => {
  const scales = useXYChartScales();
  const annotationsConfig = useXYChartAnnotationsConfig();
  const annotationsData = useXYChartAnnotationsData();
  const setState = useSetState();
  const { width } = useGridPlotSize();
  const { top: topAxisScale } = useVisibleAxesScales();
  /* Using axes from data provider instead of config ones as some axes might disappear when filtering
   * This could leave the annotations space empty and this expected to avoid suddenly moving areas around.
   */
  const { axes } = useXYChartData();
  const [behaviorTrackingProps] = useBehavioralTrackingProps(
    annotationsConfig ?? {},
  );

  const handleUpdateIndicators = (event: Partial<SelectionState>) => {
    setState((prev) => ({
      ...prev,
      annotations: {
        ...prev.annotations,
        ...event,
      },
    }));
  };

  if (isUndefined(annotationsConfig) || isUndefined(annotationsData)) {
    return null;
  }

  const {
    xAxisId,
    showLabels = false,
    annotationsActions,
    textOverflow = DEFAULT_INTEGRATION_TEXT_OVERFLOW,
  } = annotationsConfig;
  const axis = axes.find(({ id }) => id === xAxisId);

  if (axis === undefined) {
    return null;
  }

  const scale = getCurrentScale(scales, xAxisId) as ScaleLinear<
    number,
    number
  > &
    DateScales;

  const trackNames = Array.from(annotationsData.keys());

  const serializedAnnotationsData = trackNames
    .map((name) => annotationsData.get(name))
    .filter((track) => !isUndefined(track));

  const DisplayComponent = isTimeScale({
    axisType: axis.type,
    axisScale: scale,
  })
    ? AnnotationsTimeDisplay
    : AnnotationsNumericalDisplay;

  return (
    <ChartGrid.GridArea gridArea={AREAS.annotations}>
      {({ availableWidth, availableHeight }) => {
        const labelsOffset = availableWidth - width;
        const annotationsCommonConfig = {
          tracks: serializedAnnotationsData,
          showLabels: compact ? false : showLabels,
          outerBottomLine: !isUndefined(topAxisScale),
          labelsOffset,
          width: availableWidth,
          markerGroupHover: handleUpdateIndicators,
          textOverflow,
          truncateMode: 'middle' as const,
          tooltipConfig: annotationsConfig.tooltip,
          ...(compact && {
            dimensions: COMPACT_ANNOTATIONS_DIMENSIONS,
            showMarkerContent: false,
          }),
          ...behaviorTrackingProps,
        };

        return (
          <AnnotationsScrollWrapper
            height={availableHeight}
            width={availableWidth + SCROLLBAR_THRESHOLD}
          >
            <DisplayComponent
              scale={scale}
              annotationsActions={(
                annotation: AnnotationsActionsPayload | undefined,
              ) => annotation && annotationsActions?.(annotation)}
              {...annotationsCommonConfig}
            />
          </AnnotationsScrollWrapper>
        );
      }}
    </ChartGrid.GridArea>
  );
};
